import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { 
  Container, Typography, Button, Box, Card, CardContent, Grid, 
  CircularProgress, TextField, FormControlLabel, Checkbox, MenuItem, 
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, List, 
  ListItem, ListItemText, Snackbar, Alert, Accordion, AccordionSummary, AccordionDetails,
  Badge, Drawer, ListItemIcon
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AddIcon from '@mui/icons-material/Add';
import PrayerIcon from '@mui/icons-material/AccessibilityNew';
import CollectionIcon from '@mui/icons-material/LibraryBooks';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import FavoriteIcon from '@mui/icons-material/Favorite';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { MapContainer, TileLayer, Marker, Popup, ZoomControl } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { renderToString } from 'react-dom/server';

import api from '../utils/authUtils';
import { useAuth } from '../hooks/useAuth';
import { requestGeolocation, extractCoordinates } from '../utils/geolocationUtils';
import { CollectedPrayersMap } from './common';

const collectionIconHtml = renderToString(
  <LocationOnIcon style={{ color: '#FF5722', fontSize: '24px' }} />
);

const collectionIcon = new L.Icon({
  iconUrl: require('leaflet/dist/images/marker-icon-2x.png'), 
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
  shadowSize: [41, 41],
});

const prayerIcon = new L.Icon({
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
  shadowSize: [41, 41],
});

const createClusterCustomIcon = function (cluster) {
  const count = cluster.getChildCount();
  const size = Math.min(70, 40 + Math.log10(count) * 15);

  return L.divIcon({
    html: `<div class="cluster-icon">${count}</div>`,
    className: 'custom-marker-cluster',
    iconSize: L.point(size, size, true),
  });
};

function Dashboard() {
  const [uncollectedPrayers, setUncollectedPrayers] = useState([]);
  const [userData, setUserData] = useState(null);
  const [prayers, setPrayers] = useState([]);
  const [mapCenter, setMapCenter] = useState([51.505, -0.09]);
  const [mapZoom, setMapZoom] = useState(7);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dataFetched, setDataFetched] = useState(false);
  const { logout } = useAuth();
  const [createdPrayers, setCreatedPrayers] = useState([]);
  const [collectedPrayers, setCollectedPrayers] = useState([]);
  const [content, setContent] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [category, setCategory] = useState('General');
  const [editPrayerId, setEditPrayerId] = useState(null);
  const [activeDialog, setActiveDialog] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [filterCategory, setFilterCategory] = useState('');
  const [filterCollectionCount, setFilterCollectionCount] = useState([0, 100]);
  const [expandedPrayerId, setExpandedPrayerId] = useState(null);
  const [selectedPrayer, setSelectedPrayer] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationDrawerOpen, setNotificationDrawerOpen] = useState(false);
  const [isPolling, setIsPolling] = useState(true);
  const pollInterval = useRef(null);
  const mapRef = useRef(null);

  const fetchDashboardData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [userResponse, prayersResponse, createdResponse, collectedResponse, prayersWithCollectionsResponse, uncollectedPrayersResponse] = await Promise.all([
        api.get('/dashboard'),
        api.get('/prayers'),
        api.get('/prayers/user'),
        api.get('/prayers/collected'),
        api.get('/prayers/collected-details'),
        api.get('/prayers/uncollected')
      ]);

      setUserData(userResponse.data);
      setPrayers(prayersResponse.data.prayers.map(prayer => ({
        ...prayer,
        geo_location: prayer.geo_location ? JSON.parse(prayer.geo_location) : null
      })));
      setCreatedPrayers(prayersWithCollectionsResponse.data.prayers_with_collections || []);
      setCollectedPrayers(collectedResponse.data.collected_prayers || []);
      const uncollectedPrayers = uncollectedPrayersResponse.data.prayers.map(prayer => ({
        ...prayer,
        geo_location: prayer.geo_location ? JSON.parse(prayer.geo_location) : null
      }));
      setUncollectedPrayers(uncollectedPrayers);

      const firstPrayerWithLocation = prayersResponse.data.prayers.find(
        (prayer) => prayer.geo_location && JSON.parse(prayer.geo_location).coordinates &&
        JSON.parse(prayer.geo_location).coordinates.length >= 2
      );

      if (firstPrayerWithLocation) {
        const location = JSON.parse(firstPrayerWithLocation.geo_location);
        setMapCenter([location.coordinates[1], location.coordinates[0]]);
      }

      setDataFetched(true);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching dashboard data:', error);
      }
      setError('Failed to load dashboard data. Please try again.');
      if (error.response && error.response.status === 401) {
        logout();
      }
    } finally {
      setIsLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (!dataFetched) {
      fetchDashboardData();
    }
  }, [dataFetched, fetchDashboardData]);

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await api.get('/notifications');
      const notificationsData = response.data.notifications;
      setNotifications(notificationsData);
      const unreadCount = notificationsData.filter(notification => !notification.read).length;
      setUnreadCount(unreadCount);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching notifications:', error);
      }
    }
  }, []);

  useEffect(() => {
    if (isPolling) {
      fetchNotifications();
      pollInterval.current = setInterval(fetchNotifications, 30000);
    }
    return () => clearInterval(pollInterval.current);
  }, [fetchNotifications, isPolling]);

  useEffect(() => {
    const refreshInterval = setInterval(() => {
      fetchDashboardData();  // Ricarica i dati del dashboard ogni 30 secondi
    }, 30000);

    return () => clearInterval(refreshInterval); // Pulisce l'intervallo quando il componente viene smontato
  }, [fetchDashboardData]);

  const handleNotificationClick = (notification) => {
    api.post(`/notifications/${notification.id}/mark-read`)
        .then(() => {
            setNotifications(prevNotifications => prevNotifications.map(n => 
                n.id === notification.id ? { ...n, read: true } : n
            ));
            setUnreadCount(prevCount => Math.max(prevCount - 1, 0));  // Assicura che unreadCount non vada sotto 0
        })
        .catch(error => {
          if (process.env.NODE_ENV === 'development') {
            console.error('Error marking notification as read:', error);
          }
        });
};

  const handleCollectPrayer = useCallback(async (prayerId) => {
    try {
      const position = await requestGeolocation();
      const { latitude, longitude } = extractCoordinates(position);

      await api.post(`/prayers/${prayerId}/collect`, {
        latitude,
        longitude
      });

      setSnackbar({ open: true, message: 'Prayer collected successfully!', severity: 'success' });
      setDataFetched(false);
      setUncollectedPrayers(prevUncollected => prevUncollected.filter(prayer => prayer.id !== prayerId));
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error collecting prayer:', error);
      }
      // Use the user-friendly error message from geolocation utility
      const errorMessage = error.message || 'Failed to collect prayer. Please try again.';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    }
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!content) {
      setSnackbar({ open: true, message: 'Prayer content is required.', severity: 'error' });
      return;
    }

    setIsLoading(true);
    try {
      const position = await requestGeolocation();
      const { latitude, longitude } = extractCoordinates(position);

      const endpoint = editPrayerId ? `/prayers/${editPrayerId}` : '/prayers';
      const method = editPrayerId ? 'put' : 'post';

      await api[method](endpoint, {
        content,
        is_anonymous: isAnonymous,
        category,
        latitude,
        longitude
      });

      setSnackbar({ open: true, message: `Prayer ${editPrayerId ? 'updated' : 'created'} successfully!`, severity: 'success' });
      setDataFetched(false);
      resetForm();
      setActiveDialog(null);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error submitting prayer:', err);
      }
      // Use user-friendly error message
      const errorMessage = err.message || 'Failed to submit prayer.';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [content, isAnonymous, category, editPrayerId]);

  const resetForm = () => {
    setContent('');
    setIsAnonymous(false);
    setCategory('General');
    setEditPrayerId(null);
  };

  const handleEdit = (prayer) => {
    setContent(prayer.content);
    setIsAnonymous(prayer.is_anonymous);
    setCategory(prayer.category);
    setEditPrayerId(prayer.id);
    setActiveDialog('addPrayer');
  };

  const handleDelete = async (prayerId) => {
    try {
      await api.delete(`/prayers/${prayerId}`);
      setCreatedPrayers(createdPrayers.filter(prayer => prayer.id !== prayerId));
      setSnackbar({ open: true, message: 'Prayer deleted successfully!', severity: 'success' });
      setDataFetched(false);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error deleting prayer:', err);
      }
      setSnackbar({ open: true, message: 'Failed to delete prayer.', severity: 'error' });
    }
  };

  const handlePrayerClick = (prayer) => {
    setSelectedPrayer(prayer);
    if (prayer.geo_location) {
      try {
        const geoLocation = JSON.parse(prayer.geo_location);
        if (geoLocation.coordinates && geoLocation.coordinates.length === 2) {
          const [lng, lat] = geoLocation.coordinates;
          setMapCenter([lat, lng]);
          setMapZoom(12);
          mapRef.current?.flyTo([lat, lng], 12);
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error("Errore nel parsing della geolocalizzazione:", error);
        }
      }
    }
  };

  const handleAccordionToggle = (prayerId) => {
    setExpandedPrayerId(expandedPrayerId === prayerId ? null : prayerId);
    const prayer = createdPrayers.find(p => p.id === prayerId);
    setSelectedPrayer(prayer);
    if (prayer && prayer.geo_location) {
      try {
        const geoLocation = JSON.parse(prayer.geo_location);
        if (geoLocation.coordinates && geoLocation.coordinates.length === 2) {
          const [lng, lat] = geoLocation.coordinates;
          setMapCenter([lat, lng]);
          setMapZoom(12);
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error("Errore nel parsing della geolocalizzazione:", error);
        }
      }
    }
  };

  const uncollectedPrayersToDisplay = useMemo(() => {
    return uncollectedPrayers.filter(prayer => prayer.user_id !== userData?.id);
  }, [uncollectedPrayers, userData]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh" role="status">
        <CircularProgress aria-label="Loading dashboard data" />
      </Box>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert severity="error" role="alert">{error}</Alert>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setDataFetched(false)}
          aria-label="Retry loading dashboard"
          sx={{ mt: 2 }}
        >
          Retry
        </Button>
      </Container>
    );
  }

  return (
    <Box sx={{ position: 'relative', height: 'calc(100vh - 51px)', overflow: 'hidden' }}>
      {/* Barra delle notifiche */}
      <Box sx={{ position: 'fixed', top: 10, right: 10, zIndex: 1000 }}>
        <IconButton
          color="inherit"
          onClick={() => setNotificationDrawerOpen(true)}
          aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
        >
			<Badge badgeContent={unreadCount} color="error">
				<NotificationsIcon sx={{ color: 'white' }} />
			</Badge>
		</IconButton>
      </Box>

      {/* Drawer per le notifiche */}
      <Drawer
        anchor="right"
        open={notificationDrawerOpen}
        onClose={() => setNotificationDrawerOpen(false)}
        aria-label="Notifications panel"
      >
        <Box sx={{ width: 300 }}>
          <List>
            {notifications.map((notification) => (
				<ListItem 
					key={notification.id} 
					button 
					onClick={() => handleNotificationClick(notification)}
					sx={{ 
						backgroundColor: notification.read ? '#f5f5f5' : 'white', 
						textDecoration: notification.read ? 'line-through' : 'none'
					}}
				>
					<ListItemIcon>
						<NotificationsIcon color={notification.read ? 'action' : 'primary'} />
					</ListItemIcon>
					<ListItemText 
						primary={notification.message} 
						secondary={new Date(notification.created_at).toLocaleString()} 
					/>
				</ListItem>
            ))}
          </List>
        </Box>
      </Drawer>

      {/* Mappa e altri componenti */}
      <MapContainer
        center={mapCenter}
        zoom={mapZoom}
        style={{ height: '100%', width: '100%', zIndex: 1 }}
        zoomControl={false}
        ref={mapRef}
      >
        <ZoomControl position="bottomright" />
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <MarkerClusterGroup
          chunkedLoading
          iconCreateFunction={createClusterCustomIcon}
        >
          {uncollectedPrayersToDisplay.map((prayer) => (
            prayer.geo_location && prayer.geo_location.coordinates && prayer.geo_location.coordinates.length >= 2 && (
              <Marker
                key={prayer.id}
                position={[
                  prayer.geo_location.coordinates[1],
                  prayer.geo_location.coordinates[0],
                ]}
                icon={prayerIcon}
              >
                <Popup>
                  <Typography variant="body2">{prayer.content}</Typography>
                  <Typography variant="caption">
                    {prayer.is_anonymous ? 'Anonymous' : `By: ${prayer.username}`}
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleCollectPrayer(prayer.id)}
                    size="small"
                    sx={{ mt: 1 }}
                  >
                    Collect
                  </Button>
                </Popup>
              </Marker>
            )
          ))}
        </MarkerClusterGroup>
      </MapContainer>

      <Box
        sx={{
          position: 'absolute',
          top: 80,
          opacity: 0.9,
          left: 0,
          right: 0,
          padding: '10px',
          zIndex: 2,
          pointerEvents: 'none',
        }}
      >
        <Container maxWidth="md">
          <Grid container spacing={1} justifyContent="center">
            {[
              { type: 'Created', icon: <PrayerIcon />, color: '#4CAF50' },
              { type: 'Collected', icon: <CollectionIcon />, color: '#2196F3' },
              { type: 'Add', icon: <AddIcon />, color: '#FF9800' }
            ].map(({ type, icon, color }) => (
              <Grid item xs={12} sm={3.8} key={type}>
                <Card 
                  sx={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    boxShadow: 4, 
                    borderRadius: 2,
                    borderLeft: `4px solid ${color}`,
                    transition: 'transform 0.2s ease', 
                    '&:hover': { transform: 'scale(1.03)' },
                    cursor: 'pointer',
                    pointerEvents: 'auto',
                    height: '80px',
                  }}
                  onClick={() => setActiveDialog(type === 'Add' ? 'addPrayer' : type.toLowerCase() + 'Prayers')}
                >
                  <CardContent sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    width: '100%',
                    height: '100%',
                    padding: '8px !important',
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {type !== 'Add' ? (
                        <>
                          <Typography variant="h4" sx={{ fontWeight: 'bold', color: color, mr: 1, lineHeight: 1 }}>
                            {type === 'Created' ? userData?.prayers_count : userData?.collected_prayers_count || 0}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#202124', lineHeight: 1.2 }}>
                            Prayers<br />{type}
                          </Typography>
                        </>
                      ) : (
                        <Typography variant="body1" sx={{ fontWeight: 'bold', color: color, lineHeight: 1.2 }}>
                          Add<br />Prayer
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ color: color, opacity: 0.7 }}>
                      {React.cloneElement(icon, { sx: { fontSize: 32 } })}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Created Prayers Dialog */}
      <Dialog
        open={activeDialog === 'createdPrayers'}
        onClose={() => setActiveDialog(null)}
        maxWidth="xl"
        fullWidth
        aria-labelledby="created-prayers-dialog-title"
        PaperProps={{
          style: {
            borderRadius: '12px',
            overflow: 'hidden',
            height: '90vh',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <DialogTitle
          id="created-prayers-dialog-title"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid #e0e0e0',
            padding: '16px 24px',
          }}
        >
          <Typography variant="h6">Mappa delle preghiere create</Typography>
          <IconButton
            onClick={() => setActiveDialog(null)}
            aria-label="Close created prayers dialog"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ 
          display: 'flex', 
          padding: 0, 
          flex: 1,
          overflow: 'hidden',
        }}>
          <Box sx={{ 
            width: '300px', 
            borderRight: '1px solid #e0e0e0',
            overflowY: 'auto',
            padding: '16px',
            backgroundColor: '#f5f7fa',
          }}>
            <List sx={{ '& > *:not(:last-child)': { mb: 1 } }}>
              {createdPrayers.map((prayer) => (
                <ListItem 
                  key={prayer.id}
                  button
                  onClick={() => handlePrayerClick(prayer)}
                  sx={{
                    borderRadius: '4px',
                    backgroundColor: 'white',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                    padding: '12px 16px',
                    '&:hover': {
                      backgroundColor: '#f8f9fa',
                    },
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography variant="subtitle1" sx={{ fontWeight: 500, color: '#202124' }}>
                        {prayer.content}
                      </Typography>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                        <FavoriteIcon sx={{ fontSize: 14, color: 'error.main', mr: 0.5 }} />
                        <Typography variant="body2" sx={{ color: 'text.secondary', mr: 1 }}>
                          {prayer.collections.length}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {prayer.category}
                        </Typography>
                      </Box>
                    }
                  />
                  <ChevronRightIcon sx={{ color: 'action.active' }} />
                </ListItem>
              ))}
            </List>
          </Box>
          <Box sx={{ flex: 1, position: 'relative' }}>
            <MapContainer 
              center={mapCenter} 
              zoom={mapZoom} 
              style={{ height: '100%', width: '100%' }}
              ref={mapRef}
            >
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <MarkerClusterGroup
                chunkedLoading
                iconCreateFunction={createClusterCustomIcon}
              >
                {createdPrayers.map((prayer) => {
                  if (prayer.geo_location) {
                    try {
                      const geoLocation = JSON.parse(prayer.geo_location);
                      if (geoLocation.coordinates && geoLocation.coordinates.length === 2) {
                        const [lng, lat] = geoLocation.coordinates;
                        return (
                          <Marker 
                            key={`prayer-${prayer.id}`}
                            position={[lat, lng]}
                            icon={prayerIcon}
                          >
                            <Popup>
                              <Typography variant="body2">{prayer.content}</Typography>
                              <Typography variant="caption">Category: {prayer.category}</Typography>
                              <Typography variant="caption">Collections: {prayer.collections.length}</Typography>
                            </Popup>
                          </Marker>
                        );
                      }
                    } catch (error) {
                      if (process.env.NODE_ENV === 'development') {
                        console.error("Errore nel parsing della geolocalizzazione:", error);
                      }
                    }
                  }
                  return null;
                })}
                {selectedPrayer && selectedPrayer.collections.map((collection, index) => {
                  if (collection.geo_location) {
                    try {
                      const geoLocation = JSON.parse(collection.geo_location);
                      if (geoLocation.coordinates && geoLocation.coordinates.length === 2) {
                        const [lng, lat] = geoLocation.coordinates;
                        return (
                          <Marker 
                            key={`collection-${selectedPrayer.id}-${index}`} 
                            position={[lat, lng]}
                            icon={collectionIcon}
                          >
                            <Popup>
                              <Typography>Collected by: {collection.collector_username || 'Anonymous'}</Typography>
                              <Typography>Collected at: {new Date(collection.collected_at).toLocaleString()}</Typography>
                              <Typography>Coordinates: [{lat}, {lng}]</Typography>
                            </Popup>
                          </Marker>
                        );
                      }
                    } catch (error) {
                      if (process.env.NODE_ENV === 'development') {
                        console.error("Errore nel rendering del marker della collezione:", error);
                      }
                    }
                  }
                  return null;
                })}
              </MarkerClusterGroup>
            </MapContainer>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Collected Prayers Map - Full Screen Experience */}
      <CollectedPrayersMap
        prayers={collectedPrayers}
        loading={isLoading}
        open={activeDialog === 'collectedPrayers'}
        onClose={() => setActiveDialog(null)}
        title="My Spiritual Journey"
      />

      {/* Add Prayer Dialog */}
      <Dialog
        open={activeDialog === 'addPrayer'}
        onClose={() => setActiveDialog(null)}
        maxWidth="sm"
        fullWidth
        aria-labelledby="add-prayer-dialog-title"
      >
        <DialogTitle id="add-prayer-dialog-title">
          {editPrayerId ? 'Edit Prayer' : 'Add New Prayer'}
          <IconButton
            aria-label="Close dialog"
            onClick={() => {
              setActiveDialog(null);
              resetForm();
            }}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}
            aria-labelledby="add-prayer-dialog-title"
          >
            <TextField
              label="Prayer Content"
              placeholder="Enter your prayer here"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              multiline
              rows={4}
              required
              variant="outlined"
              inputProps={{
                'aria-label': 'Prayer content',
                'aria-required': 'true'
              }}
              sx={{
                '& .MuiInputBase-root': {
                  backgroundColor: '#ffffff',
                },
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: '#90caf9',
                  },
                  '&:hover fieldset': {
                    borderColor: '#42a5f5',
                  },
                 '&.Mui-focused fieldset': {
                    borderColor: '#1e88e5',
                  },
                },
              }}
            />

            <TextField
              select
              label="Category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              variant="outlined"
              inputProps={{
                'aria-label': 'Prayer category'
              }}
              sx={{
                '& .MuiInputBase-root': {
                  backgroundColor: '#ffffff',
                },
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: '#90caf9',
                  },
                  '&:hover fieldset': {
                    borderColor: '#42a5f5',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#1e88e5',
                  },
                },
              }}
            >
              <MenuItem value="General">General</MenuItem>
              <MenuItem value="Health">Health</MenuItem>
              <MenuItem value="Family">Family</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </TextField>

            <FormControlLabel
              control={
                <Checkbox
                  checked={isAnonymous}
                  onChange={(e) => setIsAnonymous(e.target.checked)}
                  inputProps={{
                    'aria-label': 'Post prayer anonymously'
                  }}
                />
              }
              label="Post as Anonymous"
            />

            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isLoading}
              aria-label={editPrayerId ? 'Update prayer' : 'Submit prayer'}
            >
              {editPrayerId ? 'Update Prayer' : 'Submit Prayer'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Dashboard;
